import React, { useMemo } from 'react';
import { InterPoint } from '@saviynt/common';
import {
  ApplicationIcon,
  Box,
  Button,
  CardFooter,
  Icon,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import type { RuleAction } from '../../../../services';

import { useInvertedLocalize } from '../../../../utils/localization';
import ruleCardMessages from '../messages';

import './RuleCardFooter.css';

interface RuleCardFooterProps {
  entitlementsCount: number;
  yearlyRequests: number;
  actions: RuleAction[]; // Rule actions containing endpoint/app information
  onViewDetails: () => void;
}

/**
 * RuleCardFooter component displaying app icons, metrics, and actions
 * in the bottom section of the rule card
 *
 * @param props - The component props
 * @returns JSX element containing the rule card footer
 */
function RuleCardFooter({
  entitlementsCount,
  yearlyRequests,
  actions,
  onViewDetails,
}: RuleCardFooterProps) {
  const l = useInvertedLocalize(ruleCardMessages);

  // Extract unique endpoints from actions array using actual API data
  const getEndpointsFromActions = (): { name: string; logo: string }[] => {
    const endpointsMap = new Map<string, { name: string; logo: string }>();

    actions.forEach((action) => {
      const { endpoint } = action.entitlementMetaData;
      const endpointName =
        endpoint.endpointDisplayName ?? endpoint.endpointName;

      // Strip .svg extension if backend mistakenly sends it
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const cleanLogo = endpoint?.logo?.endsWith('.svg')
        ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          endpoint?.logo?.slice(0, -4)
        : endpoint.logo;

      endpointsMap.set(endpointName, {
        name: endpointName,
        logo: cleanLogo,
      });
    });

    return Array.from(endpointsMap.values());
  };

  const endpoints = getEndpointsFromActions();

  // Sort endpoints: non-null logos first, preserve original order otherwise
  const sortedEndpoints = useMemo(
    () =>
      [...endpoints].sort((a, b) => {
        if (a.logo && !b.logo) {
          return -1;
        }

        if (!a.logo && b.logo) {
          return 1;
        }

        return 0;
      }),
    [endpoints]
  );

  return (
    <CardFooter className='Rules-RuleCardFooter'>
      <Box className='Rules-RuleCardFooter-content'>
        {/* Left section: Rule includes with app icons and metrics */}
        <Box className='Rules-RuleCardFooter-left'>
          <Typography kind='body2' className='Rules-RuleCardFooter-label'>
            {l('Rule Includes:')}
          </Typography>

          <Box className='Rules-RuleCardFooter-apps'>
            {/* Layered app icons */}
            <Box className='Rules-RuleCardFooter-appIcons'>
              {(() => {
                // Always show first 2 icons
                const visibleEndpoints = sortedEndpoints.slice(0, 2);
                const remainingEndpointsCount = endpoints.length - 2;
                const remainingEndpointsClasses = cx(
                  'Rules-RuleCardFooter-appsRemainingCount',
                  remainingEndpointsCount > 9 &&
                    'Rules-RuleCardFooter-appsRemainingCount--isDoubleDigit'
                );

                return (
                  <>
                    {visibleEndpoints.map((endpoint) => (
                      <ApplicationIcon
                        key={endpoint.name}
                        logo={endpoint.logo}
                        size='xxSmall'
                        className='Rules-RuleCardFooter-appIcon'
                      />
                    ))}
                    {remainingEndpointsCount > 0 && (
                      <ApplicationIcon
                        // TODO: change to: number={`+${remainingEndpointsCount}`}
                        // after DS types file is updated to included number prop
                        {...{ number: `+${remainingEndpointsCount}` }}
                        size='xxSmall'
                        classNames={{
                          presentationClassName: remainingEndpointsClasses,
                        }}
                      />
                    )}
                  </>
                );
              })()}
            </Box>

            {/* App names */}
            <Typography
              kind='body2'
              lineLimit={1}
              tooltipConfigs={{
                text: sortedEndpoints
                  .map((endpoint) => endpoint.name)
                  .join(', '), // Full text in tooltip
                placement: 'top',
              }}
              className='Rules-RuleCardFooter-appNames'>
              {sortedEndpoints.map((endpoint) => endpoint.name).join(', ')}
            </Typography>
          </Box>

          <InterPoint color='neutralBold' font='body3' />

          {/* Entitlements metric */}
          <Box className='Rules-RuleCardFooter-metric'>
            <Icon
              kind='Entitlement'
              size='small'
              color='--color-foreground-neutral-bold'
            />
            <Typography kind='body2' color='--color-foreground-neutral-medium'>
              {l(
                '{count, plural, one {# Entitlement} other {# Entitlements}}',
                { count: entitlementsCount }
              )}
            </Typography>
          </Box>

          <InterPoint color='neutralSubtle' font='body3' />

          {/* yearly requests metric */}
          <Box className='Rules-RuleCardFooter-metric'>
            <Icon
              kind='RequestAccess'
              size='small'
              color='--color-foreground-neutral-bold'
            />
            <Typography kind='body2' color='--color-foreground-neutral-medium'>
              {l(
                '{count, plural, one {# Yearly Request} other {# Yearly Requests}}',
                { count: yearlyRequests }
              )}
            </Typography>
          </Box>
        </Box>

        {/* Right section: View Details button */}
        <Box className='Rules-RuleCardFooter-right'>
          <Button
            kind='outlined'
            size='medium'
            onClick={onViewDetails}
            className='Rules-RuleCardFooter-viewButton'>
            {l('View Details')}
          </Button>
        </Box>
      </Box>
    </CardFooter>
  );
}

export default RuleCardFooter;
